import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PanelView, SceneView } from '@rendering/lookup';
import {
  Box,
  Button,
  FlexBox,
  TextInput,
  TextInputFloatingLabel,
  TextInputWithButtonBeside,
  TextInputWithFloatingLabel,
} from '@vp/swan';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useUploadContext } from 'src/hooks/UploadContextHooks';
import { useTrackingEvent } from 'src/hooks/useTrackingEvent';
import { generateBookletPreviews } from 'src/utils/BookletPreviews/generateBookletPreviews';
import { BookletPreviewType } from 'src/utils/BookletPreviews/types';
import { useBookletPreviews } from 'src/utils/BookletPreviews/useBookletPreviews';
import { BUTTON_CLICKED_EVENT } from 'src/utils/Constants';
import { text } from 'src/utils/localizationUtil';
import { noticeError } from 'src/utils/newRelicUtil';
import { fetchViews } from 'src/utils/previewUtil';
import { PlaceholderPreview } from './PlaceholderPreview';
import { AnalyticsEventData } from '../Analytics';

const PreviewCarousel = React.lazy(() =>
  import(/* webpackChunkName: "PreviewCarousel" */ './PreviewCarousel').then(mod => ({ default: mod.PreviewCarousel }))
);
const PageLabel = React.lazy(() =>
  import(/* webpackChunkName: "PageLabel" */ './PageLabel').then(mod => ({ default: mod.PageLabel }))
);

import './previewComponent.css';

const EMPTY_INPUT_STRING = '';

export const PreviewComponent = () => {
  const { upload } = useUploadContext();
  const { completedProductInformation } = useProductContext();
  const [sceneViews, setSceneViews] = useState<SceneView[]>([]);
  const [panelViews, setPanelViews] = useState<PanelView[]>([]);
  const [pageIndex, setPageIndex] = useState<number | ''>(EMPTY_INPUT_STRING);
  const [selectedPreviewIndex, setSelectedPreviewIndex] = useState<number>(0);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const previewsCarouselRef = useRef<any>(undefined);
  const { sendSegmentTrackingEvent } = useTrackingEvent();
  const { isBookletPreviewAvailable, bookletPreviewLayout } = useBookletPreviews();

  useEffect(() => {
    const loadViews = async () => {
      if (completedProductInformation) {
        try {
          const { sceneViews, panelViews } = await fetchViews(
            completedProductInformation.productKey,
            completedProductInformation.selectedOptions
          );
          setSceneViews(sceneViews);
          setPanelViews(panelViews);
        } catch (err) {
          noticeError(err, { method: 'loadViews' });
        }
      }
    };

    loadViews();
  }, [completedProductInformation]);

  useEffect(() => {
    if (isBookletPreviewAvailable) {
      setSelectedPreviewIndex(0);
    }
  }, [isBookletPreviewAvailable]);

  const bookletPreviews = useMemo(() => {
    if (sceneViews.length === 0 || !upload?.cimDocUrl || !bookletPreviewLayout || !completedProductInformation) {
      return [];
    }

    return generateBookletPreviews(sceneViews, panelViews, upload.cimDocUrl, bookletPreviewLayout);
  }, [sceneViews, upload?.cimDocUrl, bookletPreviewLayout, panelViews, completedProductInformation]);

  useEffect(() => {
    if (bookletPreviews.length === 0 || !isBookletPreviewAvailable) {
      setSelectedPreviewIndex(0);
      setPageIndex(EMPTY_INPUT_STRING);
      return;
    }

    setSelectedPreviewIndex(0);
    setPageIndex(bookletPreviews[0].pageNumbersByLabels?.[0] ?? EMPTY_INPUT_STRING);
  }, [bookletPreviews, isBookletPreviewAvailable]);

  const fullCoverPreviewIndex = useMemo(() => {
    return bookletPreviews.findIndex(page => page.type === BookletPreviewType.FullCover);
  }, [bookletPreviews]);

  const frontCoverPreviewIndex = useMemo(() => {
    return bookletPreviews.findIndex(page => page.type === BookletPreviewType.FrontCover);
  }, [bookletPreviews]);

  const backCoverPreviewIndex = useMemo(() => {
    return bookletPreviews.findIndex(page => page.type === BookletPreviewType.BackCover);
  }, [bookletPreviews]);

  const isGoButtonDisabled = useMemo(() => {
    if (pageIndex === EMPTY_INPUT_STRING) {
      return true;
    }
    if (isBookletPreviewAvailable) {
      return bookletPreviews[selectedPreviewIndex]?.pageNumbersByLabels?.includes(pageIndex as number);
    }
    return typeof pageIndex === 'number' && (pageIndex < 1 || pageIndex > panelViews.length);
  }, [pageIndex, isBookletPreviewAvailable, bookletPreviews, selectedPreviewIndex, panelViews.length]);

  // Navigate to a specific slide in the carousel
  const changeCarouselSlide = useCallback((index: number) => {
    previewsCarouselRef.current?.slickGoTo(index);
  }, []);

  // Update the page input value based on the current slide
  const updatePageInput = useCallback(
    (pageInput: number) => {
      if (isBookletPreviewAvailable) {
        if (typeof pageIndex === 'string' || !bookletPreviews[pageInput - 1].pageNumbersByLabels?.includes(pageIndex)) {
          // If the current page input is not valid for the new slide, adjust it
          const adjustedPageInput = bookletPreviews[pageInput - 1].pageNumbersByLabels?.[0] ?? EMPTY_INPUT_STRING;
          // Set the page input to empty if it's a cover page
          setPageIndex(adjustedPageInput);
        }
      } else {
        setPageIndex(pageInput);
      }
    },
    [isBookletPreviewAvailable, bookletPreviews, pageIndex]
  );

  // Navigate to the covers slide (view covers)
  const handleViewCoverClick = useCallback(
    (coverType: BookletPreviewType) => {
      let index = bookletPreviews.findIndex(page => page.type === coverType);
      if (index < 0) {
        index = coverType === BookletPreviewType.BackCover ? bookletPreviews.length - 1 : 0;
      }
      changeCarouselSlide(index);
      sendSegmentTrackingEvent({
        action: BUTTON_CLICKED_EVENT,
        label: coverType,
        category: 'Design - Upload and Print',
        variant: JSON.stringify(completedProductInformation?.selectedOptions),
      } as AnalyticsEventData);
    },
    [bookletPreviews, changeCarouselSlide, sendSegmentTrackingEvent, completedProductInformation?.selectedOptions]
  );

  const onViewCoversClick: React.MouseEventHandler<HTMLButtonElement> = () => {
    handleViewCoverClick(BookletPreviewType.FullCover);
  };

  const onViewFrontCoverClick: React.MouseEventHandler<HTMLButtonElement> = () => {
    handleViewCoverClick(BookletPreviewType.FrontCover);
  };

  const onViewBackCoverClick: React.MouseEventHandler<HTMLButtonElement> = () => {
    handleViewCoverClick(BookletPreviewType.BackCover);
  };

  // Common logic for navigating to a page in perfect or non-perfect bound
  const navigateToPage = useCallback(
    (pageNumber: number) => {
      if (isBookletPreviewAvailable) {
        let index = bookletPreviews.findIndex(({ pageNumbersByLabels }) => pageNumbersByLabels?.includes(pageNumber));
        if (index < 0) {
          // If the page number is out of range, go to the first or last page
          index = bookletPreviews.length && pageNumber > 0 ? bookletPreviews.length - 1 : 0;
        }
        changeCarouselSlide(index);
      } else {
        // Index starts with 0 so we need to subtract 1
        changeCarouselSlide(pageNumber - 1);
      }
    },
    [isBookletPreviewAvailable, bookletPreviews, changeCarouselSlide]
  );

  // Handle the "Go" button click
  const handleGoClick: React.MouseEventHandler<HTMLButtonElement> = () => {
    if (pageIndex !== EMPTY_INPUT_STRING) {
      navigateToPage(pageIndex);
      sendSegmentTrackingEvent({
        action: BUTTON_CLICKED_EVENT,
        label: 'Navigation - Go',
        category: 'Design - Upload and Print',
        variant: JSON.stringify(completedProductInformation?.selectedOptions),
      } as AnalyticsEventData);
    }
  };

  // Handle the Enter key press to navigate
  const handleEnterKey = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key.toLowerCase() === 'enter' && pageIndex !== EMPTY_INPUT_STRING) {
      navigateToPage(pageIndex);
    }
  };

  // Update the page input state based on user input
  const handlePageInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.value === EMPTY_INPUT_STRING) {
      setPageIndex(EMPTY_INPUT_STRING);
      return;
    }
    const integerValue = parseInt(event.target.value);
    // Only allow numbers to be entered
    if (isNaN(integerValue)) {
      return;
    }
    setPageIndex(integerValue);
  };

  // Callback for carousel arrow navigation
  const afterSlideChangeCb = useCallback(
    (index: number) => {
      // Page number will be the index + 1
      const pageNumber = index + 1;
      updatePageInput(pageNumber);
      setSelectedPreviewIndex(index);
    },
    [updatePageInput]
  );

  if (!upload?.cimDocUrl || sceneViews.length === 0 || (isBookletPreviewAvailable && bookletPreviews.length === 0)) {
    return <PlaceholderPreview />;
  }

  const cimDocUrl = upload.cimDocUrl;

  return (
    <FlexBox className="preview-component" flexDirection="column" padding={7}>
      <Box marginY="auto">
        <Suspense fallback={<PlaceholderPreview />}>
          <PreviewCarousel
            previewsCarouselRef={previewsCarouselRef}
            bookletPreviews={bookletPreviews}
            cimDocUrl={cimDocUrl}
            isBookletPreviewAvailable={isBookletPreviewAvailable}
            panelViews={panelViews}
            afterSlideChangeCb={afterSlideChangeCb}
          />
          <PageLabel labels={isBookletPreviewAvailable ? bookletPreviews[selectedPreviewIndex].labels : []} />
        </Suspense>
      </Box>
      <FlexBox px={7} py={6} justifyContent="center" gap="between-subsections">
        {fullCoverPreviewIndex >= 0 && (
          <Button onClick={onViewCoversClick} skin="secondary">
            {text('view-covers-button-label')}
          </Button>
        )}
        {frontCoverPreviewIndex >= 0 && (
          <Button onClick={onViewFrontCoverClick} skin="secondary">
            {text('front-cover-label')}
          </Button>
        )}
        <TextInputWithButtonBeside className="page-input">
          <TextInputWithFloatingLabel>
            <TextInput onChange={handlePageInputChange} onKeyDown={handleEnterKey} value={pageIndex} />
            <TextInputFloatingLabel>{text('page-text-input-placeholder')}</TextInputFloatingLabel>
          </TextInputWithFloatingLabel>
          <Button onClick={handleGoClick} skin="secondary" disabled={isGoButtonDisabled}>
            {text('go-button-label')}
          </Button>
        </TextInputWithButtonBeside>

        {backCoverPreviewIndex >= 0 && (
          <Button onClick={onViewBackCoverClick} skin="secondary">
            {text('back-cover-label')}
          </Button>
        )}
      </FlexBox>
    </FlexBox>
  );
};
